import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { LightboxModal } from '../components/LightboxModal';
import { SubpageHero } from '../components/SubpageHero';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { useCmsContent } from '../hooks/useCmsData';
import { SERVICES, DEFAULT_GALLERY } from '../data/company';
import { getCdnImageUrl } from '../lib/utils';

export function GalleryPage() {
  const { items: cmsGallery, loading } = useCmsContent('galleri', DEFAULT_GALLERY);
  const [selectedCategory, setSelectedCategory] = useState('alle');
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const categories = [
    { slug: 'alle', label: 'Alle projekter' },
    ...SERVICES.map(s => ({ slug: s.gallerySlug || s.slug, label: s.title }))
  ];

  const gallerySource = (cmsGallery && cmsGallery.length > 0) ? cmsGallery : DEFAULT_GALLERY;

  // Filter gallery items by category and expand images
  const rawFilteredGallery = gallerySource ? gallerySource.filter(item => {
    if (selectedCategory === 'alle') return true;
    return item.category_slug === selectedCategory;
  }) : [];

  const filteredGallery = rawFilteredGallery.flatMap((item, itemIdx) => {
    const images = Array.isArray(item.image) ? item.image : (item.image ? [item.image] : []);
    return images.map((imgUrl, imgIdx) => ({
      ...item,
      id: `${item.id || itemIdx}-${imgIdx}`,
      image: imgUrl,
    }));
  });

  return (
    <>
      <SEO 
        title="Galleri" 
        description="Se eksempler på udført arbejde og tidligere projekter fra Altmuligmanden Juelsminde." 
      />

      {/* Header Banner */}
      <SubpageHero
        badge="Udført Arbejde & Billedgalleri"
        title="Projektgalleri"
        subtitle="Gå på opdagelse i vores billeder og se eksempler på reparationer, montering, havearbejde og malerprojekter i Juelsminde og omegn."
        breadcrumbs={[
          { label: "Galleri" }
        ]}
      />

      {/* Gallery Main Area */}
      <section className="py-16 bg-sand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-10">
          
          {/* Filter Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setSelectedCategory(cat.slug)}
                className={`px-4 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                  selectedCategory === cat.slug
                    ? 'bg-forest text-white shadow-md'
                    : 'bg-white text-charcoal hover:bg-sand border border-sand-border'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Gallery Items Grid */}
          {loading ? (
            <div className="py-20 text-center space-y-3">
              <i className="fa-solid fa-spinner animate-spin text-3xl text-forest" aria-hidden="true" />
              <p className="text-muted text-sm">Henter galleribilleder...</p>
            </div>
          ) : filteredGallery.length === 0 ? (
            <div className="bg-white p-12 rounded-2xl border border-sand-border text-center max-w-md mx-auto space-y-3">
              <i className="fa-solid fa-images text-4xl text-forest/40" aria-hidden="true" />
              <h3 className="text-lg font-bold text-forest">Ingen billeder i denne kategori endnu</h3>
              <p className="text-xs text-muted">
                Der er endnu ikke tilføjet billeder til denne kategori. Vælg 'Alle projekter' eller tjek igen senere.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredGallery.map((item, idx) => {
                const imgUrl = Array.isArray(item.image) ? item.image[0] : item.image;
                if (!imgUrl) return null;

                return (
                  <div
                    key={item.id || idx}
                    onClick={() => setLightboxState({ isOpen: true, index: idx })}
                    className="bg-white rounded-2xl overflow-hidden border border-sand-border shadow-xs hover:shadow-xl transition-all duration-300 group cursor-pointer flex flex-col"
                  >
                    <div className="relative aspect-4/3 overflow-hidden bg-sand">
                      <img
                        src={getCdnImageUrl(imgUrl, 800)}
                        alt={item.title || "Galleri billede"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-forest/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <span className="bg-white text-forest font-bold text-xs px-4 py-2 rounded-full shadow-lg flex items-center gap-2 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                          <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                          <span>Klik for stor visning</span>
                        </span>
                      </div>
                    </div>

                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-bold text-forest text-base group-hover:text-copper transition-colors">
                          {item.title}
                        </h3>
                        {item.description && (
                          <p className="text-xs text-muted mt-1 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </section>

      {/* Bagside & Forside Slider Section */}
      <div id="bagside-forside" className="scroll-mt-28">
        <BeforeAfterSlider />
      </div>

      {/* Lightbox Modal */}
      {filteredGallery.length > 0 && (
        <LightboxModal
          isOpen={lightboxState.isOpen}
          image={
            Array.isArray(filteredGallery[lightboxState.index]?.image)
              ? filteredGallery[lightboxState.index]?.image[0]
              : filteredGallery[lightboxState.index]?.image
          }
          title={filteredGallery[lightboxState.index]?.title}
          description={filteredGallery[lightboxState.index]?.description}
          onClose={() => setLightboxState({ isOpen: false, index: 0 })}
          onPrev={
            filteredGallery.length > 1
              ? () =>
                  setLightboxState((prev) => ({
                    ...prev,
                    index: (prev.index - 1 + filteredGallery.length) % filteredGallery.length,
                  }))
              : null
          }
          onNext={
            filteredGallery.length > 1
              ? () =>
                  setLightboxState((prev) => ({
                    ...prev,
                    index: (prev.index + 1) % filteredGallery.length,
                  }))
              : null
          }
        />
      )}
    </>
  );
}
